import React, { useMemo } from "react";
import Moment from "react-moment";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "../../../ui-components/table/table";
import Amount from "../../../ui-components/molecules/amount/amount";
import Button, { ButtonThemes } from "../../../ui-components/button/button";
import XGSErrorMessage from "../../../ui-components/error-message/errorMessage";
import LabeledTextInput from "../../../ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import LabeledSelectInput from "../../../ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";
import { LabelModes } from "../../../ui-components/molecules/labeled-inputs/labeledInput";
import Tag from "../../../ui-components/molecules/tag/tag";
import { PaymentSummary } from "../../../app/data/payment/PaymentSession";
import { PAYMENT_STATUS_OPTIONS, statusColor } from "./paymentAdminUtils";

const columnHelper = createColumnHelper<PaymentSummary>();

export interface PaymentSearchSectionProps {
  accountNumber: string;
  invoiceNumber: string;
  status: string;
  page: number;
  totalPages: number;
  rows: PaymentSummary[];
  error: string | null;
  onAccountChange: (value: string) => void;
  onInvoiceChange: (value: string) => void;
  onStatusChange: (value: string) => void;
  onSearch: () => void;
  onPageChange: (page: number) => void;
  onRowClick: (paymentId: string) => void;
}

const PaymentSearchSection: React.FC<PaymentSearchSectionProps> = ({
  accountNumber,
  invoiceNumber,
  status,
  page,
  totalPages,
  rows,
  error,
  onAccountChange,
  onInvoiceChange,
  onStatusChange,
  onSearch,
  onPageChange,
  onRowClick,
}) => {
  const statusOption = PAYMENT_STATUS_OPTIONS.find((o) => o.value === status) ?? PAYMENT_STATUS_OPTIONS[0];

  const columns = useMemo(
    () => [
      columnHelper.accessor("dateCreated", {
        header: "Date",
        cell: (info) =>
          info.getValue() ? <Moment format="MM/DD/YYYY h:mm A">{info.getValue()}</Moment> : "—",
      }),
      columnHelper.accessor("accountNumber", { header: "Account" }),
      columnHelper.accessor("status", {
        header: "Status",
        cell: (info) => (
          <Tag mods={{ color: statusColor(info.getValue()) }}>{info.getValue()}</Tag>
        ),
      }),
      columnHelper.accessor("amountCents", {
        header: "Amount",
        cell: (info) =>
          info.getValue() != null ? <Amount amount={(info.getValue() as number) / 100} /> : "—",
      }),
      columnHelper.accessor("provider", { header: "Provider" }),
      columnHelper.accessor("invoiceNumbers", {
        header: "Invoices",
        cell: (info) => (info.getValue() || []).join(", ") || "—",
      }),
      columnHelper.accessor("paymentId", {
        header: "Payment ID",
        cell: (info) => <span className="xgs-payment-admin__id">{info.getValue()}</span>,
      }),
    ],
    []
  );

  return (
    <>
      <div className="xgs-payment-admin__filters">
        <LabeledTextInput
          label="Account"
          labelMode={LabelModes.column}
          className="xgs-payment-admin__filter-input"
          value={accountNumber}
          onChange={(e) => onAccountChange(e.target.value)}
          placeholder="Account #"
        />
        <LabeledTextInput
          label="Invoice"
          labelMode={LabelModes.column}
          className="xgs-payment-admin__filter-input"
          value={invoiceNumber}
          onChange={(e) => onInvoiceChange(e.target.value)}
          placeholder="Invoice #"
        />
        <LabeledSelectInput
          label="Status"
          labelMode={LabelModes.column}
          className="xgs-payment-admin__filter-input"
          options={PAYMENT_STATUS_OPTIONS}
          value={statusOption}
          onValueChange={(option) => onStatusChange(option?.value ?? "")}
        />
        <Button theme={ButtonThemes.blue} onClick={onSearch}>
          Search
        </Button>
      </div>
      {error && <XGSErrorMessage>{error}</XGSErrorMessage>}
      <Table
        columns={columns}
        data={rows}
        onRowClicked={(row: PaymentSummary) => onRowClick(row.paymentId)}
        cursorPointer
      />
      <div className="xgs-payment-admin__pager">
        <Button
          theme={ButtonThemes.gray}
          disabled={page <= 0}
          onClick={() => onPageChange(page - 1)}
        >
          Previous
        </Button>
        <span>
          Page {page + 1} of {Math.max(totalPages, 1)}
        </span>
        <Button
          theme={ButtonThemes.gray}
          disabled={page + 1 >= totalPages}
          onClick={() => onPageChange(page + 1)}
        >
          Next
        </Button>
      </div>
    </>
  );
};

export default PaymentSearchSection;
